import { CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { Cta } from "@/components/Cta";
import { useTranslation } from "next-i18next";

const CTASection = () => {
  const { t } = useTranslation(["home", "common"]);

  return (
    <motion.section
      className="py-20 md:py-24 relative overflow-hidden"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true, margin: "-50px" }}
    >
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/50 to-background pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            className="bg-container-background border border-border rounded-3xl overflow-hidden shadow-xl shadow-primary-600/20"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="p-8 md:p-12 lg:p-16">
              {/* Content */}
              <div className="text-center">
                <motion.h2
                  className="text-4xl md:text-5xl font-bold mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  {t('ctaSection.title')}
                </motion.h2>

                <motion.p
                  className="text-lg md:text-xl text-secondary-foreground max-w-3xl mx-auto mb-10 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  {t('ctaSection.subtitle')}
                </motion.p>

                {/* Benefits Bullet Points */}
                <motion.ul
                  className="text-left max-w-2xl mx-auto mb-10 space-y-3"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  {[0, 1, 2, 3].map((index) => (
                    <motion.li
                      key={index}
                      className="flex items-start gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <CheckCircle2
                        className="w-6 h-6 flex-shrink-0 mt-0.5"
                        style={{ color: "hsl(var(--electric-blue))" }}
                      />
                      <span className="text-primary-foreground text-base md:text-lg">
                        {t(`ctaSection.benefits.${index}`)}
                      </span>
                    </motion.li>
                  ))}
                </motion.ul>

                {/* CTA Button */}
                <motion.div
                  className="flex flex-col gap-4 justify-center items-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.9 }}
                  viewport={{ once: true }}
                >
                  <Cta size="large" primary={true}>
                    {t("Book a demo")}
                  </Cta>

                  <p className="text-sm text-secondary-foreground">
                    {t('ctaSection.tagline')}
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default CTASection;
